"use client"

import { useCallback, useEffect, useState, type ReactNode } from "react"
import { PageLoader } from "@/components/ui/page-loader"
import { getQueryClient } from "@/lib/react-query"

type RecoveryState = "checking" | "unavailable" | "signed-out"

/** NextAuth returns null on fetch errors; confirm absence before showing login. */
export function AuthSessionRecovery({ children, required = true, loading = false }: { children: ReactNode; required?: boolean; loading?: boolean }) {
  const [state, setState] = useState<RecoveryState>("checking")
  const [previousRequired, setPreviousRequired] = useState(required)
  // Reset before rendering on a new apparent signout, without remounting signup pages.
  if (previousRequired !== required) {
    setPreviousRequired(required)
    setState("checking")
  }
  const [attempt, setAttempt] = useState(0)
  const retry = useCallback(() => setAttempt(value => value + 1), [])

  useEffect(() => {
    if (!required || loading) return
    const controller = new AbortController()
    const timeout = window.setTimeout(() => controller.abort(), 15_000)
    let cancelled = false
    const verify = async () => {
      setState("checking")
      try {
        const response = await fetch("/api/identity/session", {
          credentials: "same-origin", cache: "no-store", signal: controller.signal,
        })
        // A missing session route is a broken deployment, not a transient dependency outage.
        // End the session through the independent revocation endpoint before exposing login.
        if (response.status === 404) {
          const logout = await fetch("/api/auth/logout", {
            method: "POST", credentials: "same-origin", signal: controller.signal,
          })
          if (!logout.ok) throw new Error("Unable to revoke session")
        } else if (!response.ok) {
          throw new Error("Session unavailable")
        }
        const session: unknown = response.status === 404 ? {} : await response.json()
        if (cancelled) return
        // An error object or malformed response must never be interpreted as signout.
        if (!session || typeof session !== "object" || "error" in session) throw new Error("Invalid session response")
        if ("user" in session && session.user) {
          window.location.reload()
          return
        }
        await getQueryClient().cancelQueries()
        if (cancelled) return
        getQueryClient().clear()
        setState("signed-out")
      } catch {
        if (!cancelled) setState("unavailable")
      } finally {
        window.clearTimeout(timeout)
      }
    }
    void verify()
    return () => { cancelled = true; controller.abort(); window.clearTimeout(timeout) }
  }, [attempt, required, loading])

  useEffect(() => {
    if (!required || loading || state !== "unavailable") return
    const timeout = window.setTimeout(retry, 5_000)
    window.addEventListener("online", retry)
    return () => { window.clearTimeout(timeout); window.removeEventListener("online", retry) }
  }, [state, retry, required, loading])

  if (!required) return children
  if (loading) return <SessionChecking />

  switch (state) {
    case "signed-out": return children
    case "checking": return <SessionChecking />
    case "unavailable": return <div className="flex min-h-screen items-center justify-center p-6">
      <div className="max-w-md space-y-4 text-center" role="alert">
        <p>We couldn’t verify your session. Please retry.</p>
        <button type="button" className="rounded border px-4 py-2" onClick={retry}>Retry session</button>
      </div>
    </div>
  }
}

function SessionChecking() {
  return (
    <div role="status" aria-live="polite">
      <PageLoader fullHeight size="lg" message="Checking your session…" className="min-h-dvh bg-background px-6 py-8 text-center [&_svg]:text-platform-on-light dark:[&_svg]:text-platform-on-dark" />
    </div>
  )
}
